import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import "../../styles/common/popup.scss";

const CreateCoursePopup = ({
  open,
  handleClose,
  title,
  headerTitle,
  buttonText,
  initialValue = "",
  initialActiveRecallIntervalDays = "",
  initialPassPercentage = "",
  initialTotalNumberQuizes = "",
  initialBoard = "",
  initialClassName = "",
  onSave,
}) => {
  const [inputValue, setInputValue] = useState(initialValue);
  const [activeRecallIntervalDays, setActiveRecallIntervalDays] = useState(
    initialActiveRecallIntervalDays,
  );
  const [passPercentage, setPassPercentage] = useState(initialPassPercentage);
  const [totalNumberQuizes, setTotalNumberQuizes] = useState(
    initialTotalNumberQuizes,
  );

  const normalizeOptionValue = (value) => {
    if (!value) return "";
    if (typeof value === "string") return value;
    return value?.name || value?.key || "";
  };

  const normalizedInitialBoard = normalizeOptionValue(initialBoard);
  const normalizedInitialClassName = normalizeOptionValue(initialClassName);
  const [board, setBoard] = useState(normalizedInitialBoard);
  const [classNameVal, setClassNameVal] = useState(normalizedInitialClassName);

  const isButtonEnabled = (() => {
    if (!inputValue.trim()) return false;
    if (!initialValue) return true;

    if (title === "Course") {
      return (
        inputValue !== initialValue ||
        activeRecallIntervalDays !== initialActiveRecallIntervalDays ||
        passPercentage !== initialPassPercentage ||
        totalNumberQuizes !== initialTotalNumberQuizes ||
        board !== normalizedInitialBoard ||
        classNameVal !== normalizedInitialClassName
      );
    } else if (
      title === "Subject" ||
      title === "Topic" ||
      title === "Sub Topic" ||
      title === "Subtopic"
    ) {
      return (
        inputValue !== initialValue ||
        totalNumberQuizes !== initialTotalNumberQuizes
      );
    }
    return inputValue !== initialValue;
  })();

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
  };

  const handleActiveRecallIntervalDaysChange = (e) => {
    const val = e.target.value;
    if (val === "" || /^\d+$/.test(val)) {
      setActiveRecallIntervalDays(val);
    }
  };

  const handlePassPercentageChange = (e) => {
    const val = e.target.value;
    if (val === "" || /^\d+$/.test(val)) {
      const num = parseInt(val, 10);
      if (val === "" || (num >= 0 && num <= 100)) {
        setPassPercentage(val);
      }
    }
  };

  const handleTotalNumberQuizesChange = (e) => {
    const val = e.target.value;
    if (val === "" || /^\d+$/.test(val)) {
      const num = parseInt(val, 10);
      if (val === "" || (num >= 0 && num <= 10)) {
        setTotalNumberQuizes(val);
      }
    }
  };

  const handleSave = () => {
    if (onSave) {
      if (title === "Course") {
        onSave(
          inputValue,
          activeRecallIntervalDays,
          passPercentage,
          totalNumberQuizes,
          board,
          classNameVal,
        );
      } else if (
        title === "Subject" ||
        title === "Topic" ||
        title === "Sub Topic" ||
        title === "Subtopic"
      ) {
        onSave(inputValue, totalNumberQuizes);
      } else {
        onSave(inputValue);
      }
    }
    handleClose();
  };

  useEffect(() => {
    if (open) {
      setInputValue(initialValue || "");
      setActiveRecallIntervalDays(initialActiveRecallIntervalDays || "");
      setBoard(normalizedInitialBoard);
      setClassNameVal(normalizedInitialClassName);
      if (title === "Course" && !initialValue) {
        setPassPercentage(
          initialPassPercentage !== undefined &&
            initialPassPercentage !== null &&
            initialPassPercentage !== ""
            ? String(initialPassPercentage)
            : "80",
        );
        setTotalNumberQuizes(
          initialTotalNumberQuizes !== undefined &&
            initialTotalNumberQuizes !== null &&
            initialTotalNumberQuizes !== ""
            ? String(initialTotalNumberQuizes)
            : "3",
        );
      } else {
        setPassPercentage(
          initialPassPercentage !== undefined && initialPassPercentage !== null
            ? String(initialPassPercentage)
            : "",
        );
        setTotalNumberQuizes(
          initialTotalNumberQuizes !== undefined &&
            initialTotalNumberQuizes !== null
            ? String(initialTotalNumberQuizes)
            : "",
        );
      }
    }
  }, [
    open,
    initialValue,
    initialActiveRecallIntervalDays,
    initialPassPercentage,
    initialTotalNumberQuizes,
    initialBoard,
    initialClassName,
    title,
  ]);

  return createPortal(
    <div
      className={`common-popup-overlay ${open ? "" : "hidden"} high-z-index`}
      onClick={handleClose}
    >
      <div
        className="common-popup-container"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="popup-header">
          <h2>{headerTitle || `Create ${title}`}</h2>
          <button className="popup-close-button" onClick={handleClose}>
            ×
          </button>
        </div>

        <div className="popup-content">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleSave();
            }}
          >
            <div className={`popup-form-group`}>
              <label>{title} Name</label>
              <input
                type="text"
                className="popup-input"
                value={inputValue}
                onChange={handleInputChange}
                placeholder={`Enter ${title} name`}
              />
            </div>

            {title === "Course" && (
              <>
                <div className={`popup-form-group`}>
                  <label>Board</label>
                  <input
                    type="text"
                    className="popup-input"
                    value={board}
                    onChange={(e) => setBoard(e.target.value)}
                    placeholder="Enter Board"
                  />
                </div>
                <div className={`popup-form-group`}>
                  <label>Class</label>
                  <input
                    type="text"
                    className="popup-input"
                    value={classNameVal}
                    onChange={(e) => setClassNameVal(e.target.value)}
                    placeholder="Enter Class"
                  />
                </div>
                <div className={`popup-form-group`}>
                  <label>Active Recall Interval (Days)</label>
                  <input
                    type="number"
                    min="0"
                    className="popup-input"
                    value={activeRecallIntervalDays}
                    onChange={handleActiveRecallIntervalDaysChange}
                    placeholder="Enter active recall interval in days"
                  />
                </div>
                <div className={`popup-form-group`}>
                  <label>Pass Percentage (%)</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    className="popup-input"
                    value={passPercentage}
                    onChange={handlePassPercentageChange}
                    placeholder="Enter pass percentage (0 - 100)"
                  />
                </div>
                <div className={`popup-form-group`}>
                  <label>
                    Number of quizzes (after completion of the content page)
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="10"
                    className="popup-input"
                    value={totalNumberQuizes}
                    onChange={handleTotalNumberQuizesChange}
                    placeholder="Enter number of quizzes (0 - 10)"
                  />
                </div>
              </>
            )}

            {(title === "Subject" ||
              title === "Topic" ||
              title === "Sub Topic" ||
              title === "Subtopic") && (
              <div className={`popup-form-group`}>
                <label>
                  Number of quizzes (after completion of the content page)
                </label>
                <input
                  type="number"
                  min="0"
                  max="10"
                  className="popup-input"
                  value={totalNumberQuizes}
                  onChange={handleTotalNumberQuizesChange}
                  placeholder="Enter number of quizzes (0 - 10)"
                />
                {title === "Subject" && (
                  <small
                    className="popup-hint"
                    style={{
                      fontSize: "12px",
                      color: "#666",
                      marginTop: "4px",
                      display: "block",
                    }}
                  >
                    This count will reflect for the content page created against
                    the subject. If not mentioned here, then course count will
                    be taken as default.
                  </small>
                )}
                {title === "Topic" && (
                  <small
                    className="popup-hint"
                    style={{
                      fontSize: "12px",
                      color: "#666",
                      marginTop: "4px",
                      display: "block",
                    }}
                  >
                    This count will reflect for the content page created against
                    the topic. If not mentioned here, then course count will be
                    taken as default.
                  </small>
                )}
                {(title === "Sub Topic" || title === "Subtopic") && (
                  <small
                    className="popup-hint"
                    style={{
                      fontSize: "12px",
                      color: "#666",
                      marginTop: "4px",
                      display: "block",
                    }}
                  >
                    This count will reflect for the content page created against
                    the subtopic. If not mentioned here, then course count will
                    be taken as default.
                  </small>
                )}
              </div>
            )}

            <div className="popup-actions">
              <button
                type="submit"
                className="popup-submit-button"
                disabled={!isButtonEnabled}
              >
                {buttonText || "Create"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default CreateCoursePopup;
